import { hasIntegrationAtom, integrationCallbacksAtom } from '@/atoms/integrationAtoms';
import { DEFAULT_DATE_FORMAT } from '@/components/constants';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import {
  isSessionsSelectedAllAtom,
  selectedSessionIdsAtom,
  useSessionsList,
} from '@/components/layout/Navigation/sessions/side-nav-list/hooks/useSessionsList';
import { DatadogDurationVitalStopper } from '@/components/utils/datadogDurationVitalStopper';
import { useGetPageSize } from '@/hooks/query/useQuerySessionsV2';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useSessionsListHotkeys } from '@/hooks/useSessionsListHotkeys';
import { useTutorial } from '@/hooks/useTutorial';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { convertMomentToDateFnsFormat } from '@/lib/parseTimeStamp';
import { getGregorianCalendarLocale } from '@/utils/dates';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { VList, type VListHandle } from 'virtua';
import { useSessionsFilteredList } from '../../hooks/useSessionsFilteredList';
import { SessionsListItem } from './sessions-list-item';

const SESSION_LIST_SSR_COUNT = 24;
const SESSION_LIST_FETCH_MORE_THRESHOLD = 3;
// Measured row height; accurate estimates minimize virtua's backward-scroll corrections.
const SESSION_LIST_ESTIMATED_ITEM_SIZE = 46;

export const SessionsList = () => {
  const intl = useIntl();
  const { sessionSearchResult, searchValue } = useSessionsFilteredList();
  const {
    sessions,
    loadedSessionCount,
    handleSessionSelection,
    onSessionDeleted,
    allSessionIds,
    handleDeleteSelected,
    isFetchingNextPage,
    isFetching: isSessionsFetching,
    fetchNextPage,
    hasNextPage,
  } = useSessionsList();

  const vListRef = useRef<VListHandle>(null);

  const { data: userData } = useQueryUser((data) => ({
    session_settings: data?.session_settings,
  }));

  const dateFormat = useMemo(() => {
    const rawDateFormat = userData?.session_settings?.date_format || DEFAULT_DATE_FORMAT;
    return convertMomentToDateFnsFormat(rawDateFormat);
  }, [userData]);
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(getGregorianCalendarLocale(intl.locale), {
        hour: 'numeric',
        minute: '2-digit',
      }),
    [intl.locale]
  );

  const { tutorialStepCurrentPage, panelOpen } = useTutorial();
  const shouldHighlightFirstSession =
    tutorialStepCurrentPage?.enum === TemplatePages.NAVIGATE_BACK_TO_SESSION && panelOpen;

  const pageSize = useGetPageSize();

  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const integrationCallbacks = useAtomValue(integrationCallbacksAtom);

  const isMobile = useIsMobile();

  const [selectedSessionIds, setSelectedSessionIds] = useAtom(selectedSessionIdsAtom);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);

  useSessionsListHotkeys({
    vListRef,
    allSessionIds,
    handleDeleteSelected,
    selectedSessionIds,
  });

  const isSessionsSelectedAll = useAtomValue(isSessionsSelectedAllAtom);
  const integrationSwitchPatient = hasIntegration
    ? integrationCallbacks.onSwitchPatient
    : undefined;
  const showCheckbox = selectedSessionIds.length > 0;

  useEffect(() => {
    if (isSessionsSelectedAll && !isSessionsFetching) {
      setSelectedSessionIds(
        sessionSearchResult
          ?.filter((session) => !session.is_offline_synthetic)
          .map((session) => session.session_id) ?? []
      );
    }
  }, [
    isSessionsFetching,
    sessionSearchResult,
    isSessionsSelectedAll,
    setSelectedSessionIds,
  ]);

  if (!sessionSearchResult || !sessions) return <></>;

  const sessionListItemCount = sessionSearchResult.length + (isFetchingNextPage ? 1 : 0);
  const sessionListSsrCount = Math.min(sessionListItemCount, SESSION_LIST_SSR_COUNT);

  // While searching, the short filtered list keeps the range at its end: fetching would chain-load every page.
  // cancelRefetch: false makes concurrent range callbacks no-ops while a page is in flight.
  const handleRangeChange = (_startIndex: number, endIndex: number) => {
    if (
      endIndex < sessionListItemCount - SESSION_LIST_FETCH_MORE_THRESHOLD ||
      Boolean(searchValue) ||
      !fetchNextPage ||
      !hasNextPage ||
      isFetchingNextPage ||
      !loadedSessionCount ||
      // The LOADED length, not the displayed one. A read filter (queued offline
      // deletes) can shrink `sessions` below a full page without the server
      // having run out — reading `sessions.length` here would treat that as
      // "no more pages" and strand a clinician on their first page forever.
      loadedSessionCount < pageSize
    ) {
      return;
    }
    void fetchNextPage({ cancelRefetch: false });
  };

  return (
    <>
      {sessionSearchResult.length > 0 && (
        <DatadogDurationVitalStopper
          condition={!isSessionsFetching}
          vitalName={[
            DatadogDurationVital.SESSION_SEARCH_RENDERED_V3,
            DatadogDurationVital.SESSION_SEARCH_RENDERED_V2,
            DatadogDurationVital.SESSION_SEARCH_RENDERED_V1,
          ]}
          disableRafBehaviour
        />
      )}
      <VList
        data-testid="session-list-session-list"
        count={sessionListItemCount}
        ssrCount={sessionListSsrCount}
        itemSize={SESSION_LIST_ESTIMATED_ITEM_SIZE}
        onRangeChange={handleRangeChange}
        className={cn('scrollbar-fade min-h-0', isMobile ? 'h-full pb-8' : 'h-full')}
        ref={vListRef}
      >
        {(i) => {
          const session = sessionSearchResult.at(i);
          return session ? (
            <SessionsListItem
              dateFormat={dateFormat}
              locale={intl.locale}
              timeFormatter={timeFormatter}
              handleSessionSelection={handleSessionSelection}
              index={i}
              integrationSwitchPatient={integrationSwitchPatient}
              isSelected={selectedSessionIds.includes(session.session_id)}
              isFetchingNextPage={isFetchingNextPage}
              isMobile={isMobile}
              onSessionDeleted={onSessionDeleted}
              session={session}
              sessionSearchResult={sessionSearchResult}
              sessions={sessions}
              setIsMobileHeaderSheetOpen={setIsMobileHeaderSheetOpen}
              setSelectedSessionIds={setSelectedSessionIds}
              showCheckbox={showCheckbox}
              shouldHighlightFirstSession={shouldHighlightFirstSession}
            />
          ) : (
            <></>
          );
        }}
      </VList>
    </>
  );
};
